import React, { useState } from 'react';

const SYLLABI = {
  python: {
    color: '#1a2e5a',
    accent: '#2563eb',
    emoji: '🐍',
    tagline: 'From zero to building real programs — with projects every single week.',
    outcome: 'By the end: You can build working Python apps, automate tasks, work with data, and call real-world APIs.',
    weeks: [
      {
        week: 'Week 1',
        title: 'Python Foundations',
        tag: 'Setup & First Programs',
        bullets: ['Install Python + VS Code', 'Variables, data types, f-strings', 'User input & arithmetic operators'],
        project: 'Simple Calculator',
      },
      {
        week: 'Week 2',
        title: 'Control Flow',
        tag: 'Logic & Decisions',
        bullets: ['if / elif / else conditions', 'for & while loops, range()', 'break, continue, nested loops'],
        project: 'Number Guessing Game',
      },
      {
        week: 'Week 3',
        title: 'Functions & Modules',
        tag: 'Clean, Reusable Code',
        bullets: ['def, parameters, return values', 'Scope, default args, *args', 'Standard library: math, random, datetime'],
        project: 'Password Generator',
      },
      {
        week: 'Week 4',
        title: 'Data Structures',
        tag: 'Lists, Dicts, Sets, Tuples',
        bullets: ['List methods + list comprehensions', 'Dictionaries — CRUD & nested', 'When to use which structure'],
        project: 'Student Report Card System',
      },
      {
        week: 'Week 5',
        title: 'File Handling & Error Management',
        tag: 'Real-World Robustness',
        bullets: ['Read/write files, CSV & JSON', 'try / except / finally blocks', 'Building programs that don\'t crash'],
        project: 'Contact Book (saves to JSON)',
      },
      {
        week: 'Week 6',
        title: 'Object-Oriented Programming',
        tag: 'Think Like a Developer',
        bullets: ['Classes, objects, __init__', 'Inheritance & method overriding', 'Encapsulation & __str__'],
        project: 'Library Management System',
      },
      {
        week: 'Week 7',
        title: 'Libraries, APIs & Automation',
        tag: 'What Python is Famous For',
        bullets: ['pip, requests, pandas basics', 'Fetch real data from live APIs', 'File automation & web scraping intro'],
        project: 'Live Weather App using API',
      },
      {
        week: 'Week 8',
        title: 'Capstone Project & Career Path',
        tag: 'Build + Present + What\'s Next',
        bullets: ['Plan & build your own project', 'Code review, testing, documentation', 'GitHub portfolio + career roadmap'],
        project: 'Your Capstone — Demo Day 🎤',
      },
    ],
  },
  webdev: {
    color: '#7c1020',
    accent: '#dc2626',
    emoji: '🌐',
    tagline: 'From "how does a website work?" to a live deployed portfolio in 8 weeks.',
    outcome: 'By the end: You\'ve built and deployed a real portfolio website that anyone in the world can visit.',
    weeks: [
      {
        week: 'Week 1',
        title: 'HTML Foundations',
        tag: 'How the Web Works',
        bullets: ['How browsers render pages (client/server)', 'HTML document structure & semantic tags', 'Forms, links, images, tables'],
        project: 'Structured "About Me" Page',
      },
      {
        week: 'Week 2',
        title: 'CSS — Design & Styling',
        tag: 'Box Model, Colors, Fonts',
        bullets: ['Selectors, cascade, specificity', 'Box model: padding, border, margin', 'Google Fonts + color systems (hex, hsl)'],
        project: 'Styled Profile Card',
      },
      {
        week: 'Week 3',
        title: 'CSS Layouts',
        tag: 'Flexbox + Grid',
        bullets: ['Flexbox: nav bars, card rows, centering', 'CSS Grid: page layouts, responsive columns', 'CSS variables + transitions + hover effects'],
        project: 'Full Blog Layout',
      },
      {
        week: 'Week 4',
        title: 'Responsive Design',
        tag: 'Mobile-First Websites',
        bullets: ['Media queries & breakpoints', 'Mobile-first workflow', 'CSS animations with @keyframes'],
        project: 'Responsive Landing Page',
      },
      {
        week: 'Week 5',
        title: 'JavaScript Basics',
        tag: 'Bringing Pages to Life',
        bullets: ['let/const, data types, functions, loops', 'DOM — selecting & changing elements', 'Events: click, input, keydown'],
        project: 'Interactive To-Do List',
      },
      {
        week: 'Week 6',
        title: 'Modern JavaScript',
        tag: 'Arrays, APIs, localStorage',
        bullets: ['.map(), .filter(), destructuring', 'fetch() + async/await + JSON parsing', 'localStorage — apps that remember data'],
        project: 'Movie Wishlist App (with API)',
      },
      {
        week: 'Week 7',
        title: 'Real Dev Tools & Practices',
        tag: 'How Developers Actually Work',
        bullets: ['Git & GitHub — version control basics', 'Tailwind CSS intro + DevTools deep dive', 'Accessibility, SEO & Lighthouse audit'],
        project: 'Portfolio: Plan + Build',
      },
      {
        week: 'Week 8',
        title: 'Deploy & Demo Day',
        tag: 'Go Live + Career Roadmap',
        bullets: ['Deploy on GitHub Pages / Netlify (free)', 'Portfolio presentation to the batch', 'Frontend → React.js, Backend, Freelancing'],
        project: 'Live Portfolio on the Internet 🚀',
      },
    ],
  },
  msoffice: {
    color: '#1a5a2e',
    accent: '#16a34a',
    emoji: '💼',
    tagline: 'From switching on a computer to building professional documents, spreadsheets, and presentations.',
    outcome: 'By the end: You can confidently use Word, Excel, PowerPoint, Google Workspace, and AI tools — skills every job demands.',
    weeks: [
      {
        week: 'Week 1',
        title: 'Computer Basics & System Operations',
        tag: 'Starting from Scratch',
        bullets: [
          'Parts of a computer — hardware vs software',
          'Windows: desktop, taskbar, file explorer, settings',
          'Creating folders, copy/paste, rename, delete & recycle bin',
          'Essential keyboard shortcuts (Ctrl+C/V/Z/S, Alt+Tab, Win+D)',
        ],
        project: 'Organise Your Computer — create a proper folder structure for studies',
      },
      {
        week: 'Week 2',
        title: 'Microsoft Word — Basics',
        tag: 'Documents That Look Professional',
        bullets: [
          'Creating, saving, and opening Word files (.docx)',
          'Formatting: fonts, size, bold/italic, alignment, line spacing',
          'Inserting images, tables, page numbers & headers/footers',
          'Find & Replace, spell check, word count',
        ],
        project: 'Your Biodata / Resume in Word',
      },
      {
        week: 'Week 3',
        title: 'Microsoft Word — Advanced',
        tag: 'Reports, Letters & Templates',
        bullets: [
          'Styles & Headings — automatic Table of Contents',
          'Bullets, numbered lists, multi-column layouts',
          'Mail Merge basics — one letter sent to many people',
          'Printing, PDF export, sharing & protecting documents',
        ],
        project: 'Formal Application Letter + Internship Report Template',
      },
      {
        week: 'Week 4',
        title: 'Microsoft Excel — Basics',
        tag: 'Spreadsheets from Zero',
        bullets: [
          'Rows, columns, cells — entering and formatting data',
          'Basic formulas: SUM, AVERAGE, COUNT, MIN, MAX',
          'Cell references: relative vs absolute ($A$1)',
          'Sorting, filtering, and freezing rows/columns',
        ],
        project: 'Class Marks Sheet with Totals, Averages & Pass/Fail',
      },
      {
        week: 'Week 5',
        title: 'Microsoft Excel — Advanced',
        tag: 'Real Data & Charts',
        bullets: [
          'IF, COUNTIF, SUMIF — conditional logic in cells',
          'VLOOKUP / XLOOKUP — finding data across sheets',
          'Charts: bar, pie, line — from raw data to visuals',
          'Pivot Tables intro — summarise 1000 rows in seconds',
        ],
        project: 'Monthly Expense Tracker with Charts',
      },
      {
        week: 'Week 6',
        title: 'Microsoft PowerPoint',
        tag: 'Presentations That Impress',
        bullets: [
          'Slide layouts, themes, master slides',
          'Text, images, shapes, icons & SmartArt',
          'Transitions & animations — professional vs over-the-top',
          'Presenter view, slide notes, presenting to an audience',
        ],
        project: '10-Slide Presentation on Any Topic of Your Choice',
      },
      {
        week: 'Week 7',
        title: 'Google Docs & Google Sheets',
        tag: 'Work from Anywhere — Free & Collaborative',
        bullets: [
          'Google Docs vs Word — what\'s different, how to switch',
          'Real-time collaboration — share, comment, suggest edits',
          'Google Sheets: formulas, charts, conditional formatting',
          'Google Forms — create surveys and collect responses into Sheets',
        ],
        project: 'Shared Budget Planner in Google Sheets + Google Form for collecting data',
      },
      {
        week: 'Week 8',
        title: 'AI Tools in Office & Productivity',
        tag: 'Work 10× Faster with AI',
        bullets: [
          'ChatGPT for writing: drafting emails, letters, reports in seconds',
          'AI formulas in Google Sheets — describe a formula, AI writes it',
          'Canva for presentations — AI-generated slides and designs',
          'Gmail, Google Drive, Calendar — managing your digital life',
        ],
        project: 'Final Capstone: Resume (Word) + Budget Sheet (Excel/Sheets) + Presentation (PPT) — presented to the batch 🎤',
      },
    ],
  },
};

const TOOLS = {
  python: ['Python 3', 'VS Code', 'pip', 'requests', 'pandas', 'GitHub', 'Free APIs'],
  webdev: ['HTML5', 'CSS3', 'JavaScript', 'Tailwind CSS', 'Git & GitHub', 'Netlify', 'Chrome DevTools'],
  msoffice: ['MS Word', 'MS Excel', 'MS PowerPoint', 'Google Docs', 'Google Sheets', 'Google Forms', 'ChatGPT', 'Canva'],
};

export default function SyllabusPage() {
  const [active, setActive] = useState('python');
  const [expanded, setExpanded] = useState(null);
  const course = SYLLABI[active];

  return (
    <div style={{ paddingTop: 64, fontFamily: 'Nunito, sans-serif' }}>

      {/* Header */}
      <section style={{ background: 'var(--cream)', padding: '56px 24px 44px', textAlign: 'center' }}>
        <div className="badge">📖 Detailed Curriculum</div>
        <h1 className="section-title" style={{ textAlign: 'center' }}>Course Syllabus</h1>
        <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center', maxWidth: 560 }}>
          8 weeks · 3–4 classes/week · 1 hr each · Real projects every week
        </p>

        {/* Tab Switcher */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 32, flexWrap: 'wrap' }}>
          {[
            ['python',
              <>
                <img src="/python.png" alt="Python" style={{ height: 20, marginRight: 8 }} />
                Python Programming
              </>
            ],
            ['webdev', '🌐 Web Development'],
            ['msoffice', '💼 MS Office & AI Tools'],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => { setActive(id); setExpanded(null); }}
              style={{
                padding: '12px 30px',
                borderRadius: 50,
                border: `2px solid ${active === id ? SYLLABI[id].color : '#ddd'}`,
                background: active === id ? SYLLABI[id].color : '#fff',
                color: active === id ? '#fff' : '#555',
                fontWeight: 800,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'Nunito, sans-serif',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      {/* Course Banner */}
      <section style={{ background: course.color, padding: '28px 24px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: 15, marginBottom: 8 }}>{course.tagline}</p>
          <p style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>✅ {course.outcome}</p>
        </div>
      </section>

      {/* Tools You'll Learn */}
      <section style={{ background: '#f8f8f8', borderBottom: '1px solid #eee', padding: '18px 24px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontWeight: 700, color: 'var(--gray)', fontSize: 13, marginRight: 4 }}>TOOLS YOU'LL USE:</span>
          {TOOLS[active].map(t => (
            <span key={t} style={{
              background: '#fff', border: `1.5px solid ${course.color}`, color: course.color,
              padding: '4px 14px', borderRadius: 50, fontSize: 13, fontWeight: 700,
            }}>{t}</span>
          ))}
        </div>
      </section>

      {/* Week Cards */}
      <section className="section" style={{ paddingTop: 40 }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px' }}>

          {course.weeks.map(({ week, title, tag, bullets, project }, i) => {
            const isOpen = expanded === i;
            return (
              <div
                key={week}
                onClick={() => setExpanded(isOpen ? null : i)}
                style={{
                  background: '#fff',
                  border: `2px solid ${isOpen ? course.color : '#e8e8e8'}`,
                  borderRadius: 16,
                  marginBottom: 12,
                  cursor: 'pointer',
                  transition: 'all 0.22s',
                  boxShadow: isOpen ? `0 4px 20px rgba(0,0,0,0.10)` : '0 1px 4px rgba(0,0,0,0.05)',
                  overflow: 'hidden',
                }}
              >
                {/* Header Row */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '18px 22px', gap: 14 }}>
                  <span style={{
                    background: isOpen ? course.color : '#f0f0f0',
                    color: isOpen ? '#fff' : course.color,
                    padding: '4px 13px', borderRadius: 50,
                    fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
                    transition: 'all 0.2s', flexShrink: 0,
                  }}>
                    {week}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 16, lineHeight: 1.3 }}>{title}</div>
                    <div style={{ color: 'var(--gray)', fontSize: 13, marginTop: 2 }}>{tag}</div>
                  </div>
                  {/* Project badge */}
                  <span style={{
                    background: '#fffbea', border: '1.5px solid #f0c040',
                    color: '#7a5800', padding: '3px 11px', borderRadius: 50,
                    fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                    display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
                  }}>
                    🛠 Project
                  </span>
                  {/* Chevron */}
                  <span style={{
                    fontSize: 18, color: course.color, fontWeight: 700,
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s', flexShrink: 0, marginLeft: 4,
                  }}>▾</span>
                </div>

                {/* Expanded Detail */}
                {isOpen && (
                  <div style={{ borderTop: `1px solid #eee`, padding: '16px 22px 20px', background: '#fafafa' }}>
                    <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--gray)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>What You'll Learn</div>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {bullets.map(b => (
                            <li key={b} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 7, fontSize: 14, color: '#333', lineHeight: 1.5 }}>
                              <span style={{ color: course.accent, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>→</span>
                              {b}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div style={{ minWidth: 200, maxWidth: 220 }}>
                        <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--gray)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Week's Project</div>
                        <div style={{
                          background: `linear-gradient(135deg, ${course.color}15, ${course.color}05)`,
                          border: `1.5px solid ${course.color}30`,
                          borderRadius: 12, padding: '14px 16px',
                        }}>
                          <div style={{ fontSize: 22, marginBottom: 6 }}>🛠️</div>
                          <div style={{ fontWeight: 800, color: course.color, fontSize: 14, lineHeight: 1.4 }}>{project}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <p style={{ textAlign: 'center', color: 'var(--gray)', fontSize: 13, margin: '8px 0 32px' }}>
            👆 Tap any week to see what's inside
          </p>

          {/* What You Get */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
            {[
              ['📦', '8 Mini-Projects', 'One real project built every week — not just theory'],
              ['📄', 'Class Handouts', 'Printed/PDF notes for every session to take home'],
              [<img src="/whatsapp.png" alt="WhatsApp" style={{ height: 32 }} />, 'WhatsApp Support', 'Ask doubts anytime between classes'],
              ['🎓', 'Certificate', 'On completing all 8 weeks + your final capstone project'],
            ].map(([icon, title, desc]) => (
              <div key={title} style={{
                background: '#fff', border: '2px solid #eee', borderRadius: 16,
                padding: '20px 18px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
                <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 14, marginBottom: 4 }}>{title}</div>
                <div style={{ color: 'var(--gray)', fontSize: 13, lineHeight: 1.5 }}>{desc}</div>
              </div>
            ))}
          </div>

          {/* Certificate Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #fffbea, #fff8d6)',
            border: '2px solid #f0c040', borderRadius: 20, padding: 28,
            textAlign: 'center', marginBottom: 20,
          }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🎓</div>
            <h3 style={{ color: 'var(--navy)', fontWeight: 900, fontSize: 18, marginBottom: 8 }}>
              Certificate of Completion
            </h3>
            <p style={{ color: 'var(--gray)', fontSize: 15, maxWidth: 420, margin: '0 auto' }}>
              Awarded after finishing all 8 weeks and submitting your final project.
              Add it to your resume and LinkedIn — it carries real weight.
            </p>
          </div>

          {/* Download Note */}
          <div style={{
            background: course.color, borderRadius: 20, padding: 28, textAlign: 'center',
          }}>
            <p style={{ color: 'rgba(255,255,255,0.82)', fontSize: 15, marginBottom: 18 }}>
              Want the detailed syllabus with full class-by-class breakdown? WhatsApp us and we'll send it instantly.
            </p>
            <button
              className="btn-gold"
              onClick={() => window.open(`https://wa.me/919000000000?text=${encodeURIComponent('Hi! Please send me the VRK Tech Academy detailed syllabus.')}`, '_blank')}
            >
              💬 Get Detailed Syllabus on WhatsApp
            </button>
          </div>

        </div>
      </section>
    </div>
  );
}